/**
 * User Management State Atoms
 *
 * Jotai atoms for user filters, selection, editing state, and modal state.
 */

import { atom } from 'jotai';
import { atomWithStorage } from 'jotai/utils';
import type { UserFilter, UserRole } from '@/schemas/user';
import { currentFactoryIdAtom } from '@/stores/atoms/factory.atoms';

// ============================================================================
// Filter Atoms
// ============================================================================

export const userFiltersAtom = atomWithStorage<UserFilter>(
  'user-filters',
  { limit: 100 },
  undefined,
  { getOnInit: true }
);

export const userSearchQueryAtom = atom('');
export const userRoleFilterAtom = atom(null as UserRole | null);
export const userStatusFilterAtom = atom(null as 'active' | 'inactive' | null);

/** Combined filters for API query — always scoped to the currently selected factory */
export const userQueryFiltersAtom = atom(get => {
  const baseFilters = get(userFiltersAtom);
  const searchQuery = get(userSearchQueryAtom);
  const roleFilter = get(userRoleFilterAtom);
  const statusFilter = get(userStatusFilterAtom);
  const factoryId = get(currentFactoryIdAtom);

  return {
    ...baseFilters,
    ...(searchQuery && { search: searchQuery }),
    ...(roleFilter && { role: roleFilter }),
    ...(statusFilter && { status: statusFilter }),
    ...(factoryId && { factoryId }),
  } as UserFilter;
});

// ============================================================================
// Selection Atoms
// ============================================================================

export const selectedUserIdsAtom = atom<Set<string>>(new Set<string>());

export const isUserSelectedAtom = atom(get => (userId: string): boolean => {
  return get(selectedUserIdsAtom).has(userId);
});

export const selectedUserCountAtom = atom(get => get(selectedUserIdsAtom).size);

export const toggleUserSelectionAtom = atom(null, (get, set, userId: string) => {
  const selected = get(selectedUserIdsAtom);
  const newSelected = new Set(selected);
  if (newSelected.has(userId)) {
    newSelected.delete(userId);
  } else {
    newSelected.add(userId);
  }
  set(selectedUserIdsAtom, newSelected);
});

export const clearUserSelectionAtom = atom(null, (_get, set) => {
  set(selectedUserIdsAtom, new Set());
});

export const selectAllUsersAtom = atom(null, (_get, set, userIds: string[]) => {
  set(selectedUserIdsAtom, new Set(userIds));
});

// ============================================================================
// Inline Editing State
// ============================================================================

export interface EditingCell {
  userId: string;
  field: 'firstName' | 'lastName' | 'email' | 'phoneNumber';
}

export const editingCellAtom = atom(null as EditingCell | null);

export const isEditingAtom = atom(get => (userId: string, field: EditingCell['field']): boolean => {
  const editing = get(editingCellAtom);
  return editing !== null && editing.userId === userId && editing.field === field;
});

// ============================================================================
// User Detail Modal State
// ============================================================================

export interface UserDetailModalState {
  isOpen: boolean;
  userId: string | null;
  mode: 'view' | 'edit';
}

export const userDetailModalAtom = atom<UserDetailModalState>({
  isOpen: false,
  userId: null,
  mode: 'view',
});

export const openUserDetailModalAtom = atom(
  null,
  (_get, set, { userId, mode = 'view' }: { userId: string; mode?: 'view' | 'edit' }) => {
    set(userDetailModalAtom, {
      isOpen: true,
      userId,
      mode,
    });
  }
);

export const closeUserDetailModalAtom = atom(null, (_get, set) => {
  set(userDetailModalAtom, {
    isOpen: false,
    userId: null,
    mode: 'view',
  });
});

export const setUserDetailModalModeAtom = atom(null, (get, set, mode: 'view' | 'edit') => {
  const current = get(userDetailModalAtom);
  set(userDetailModalAtom, {
    ...current,
    mode,
  });
});

// ============================================================================
// Bulk Action State
// ============================================================================

export const isBulkActionActiveAtom = atom(get => get(selectedUserCountAtom) > 0);

// ============================================================================
// Reset Helpers
// ============================================================================

export const resetUserFiltersAtom = atom(null, (_get, set) => {
  set(userFiltersAtom, { limit: 100 });
  set(userSearchQueryAtom, '');
  set(userRoleFilterAtom, null);
  set(userStatusFilterAtom, null);
});

export const resetUserSelectionAtom = atom(null, (_get, set) => {
  set(selectedUserIdsAtom, new Set<string>());
});

export const resetUserStateAtom = atom(null, (_get, set) => {
  set(selectedUserIdsAtom, new Set<string>());
  set(editingCellAtom, null);
  set(userDetailModalAtom, {
    isOpen: false,
    userId: null,
    mode: 'view',
  });
});
